import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Tooltip, IconButton } from "@mui/material";
import { useParams } from "react-router-dom";
import { useForm } from "react-hook-form";
import TabGridAndFormLayout from "../../../Components/ProForms/ProFormsLayout/TabGridAndFormLayout";
import ProGrid from "../../../Components/ProGrid/v2";
import DeleteCell from "../../../Components/ProGrid/components/DeleteCell";
import ProSelectField from "../../../Components/Inputs/SelectField";
import ProTextInput from "../../../Components/Inputs/TextField";
import { STAUTES } from "../../../../utils/constant";
import usePageState from "../../../../utils/customHooks/usePageState";
import {
  ADD_EMPLOYEE_DOCUMENT,
  UPDATE_EMPLOYEE_DOCUMENT,
  GET_EMPLOYEE_DOCUMENTS_BY_ID,
  EMPLOYEE_DOCUMENTS,
  EMPLOYEE_DOCUMENT_TYPES
} from "../../../../utils/services/apiPath";
import { postApi, deleteApi, getApi } from "../../../../utils/services";
import {
  prepareDefaultValues,
  employeeDocumentsFormName,
  prepareInitialConfig,
  fieldTruePermission,
  convertFileToBase64Model,
  changeModeForField,
  changeMode
} from "../../../../utils/formHelper";
import { employeesDocumentsPermissionSelector } from "../../../store/features/permissions/permissionsSlice";
import usePermission from "../../../../utils/customHooks/usePermission";
import useColumn from "../../../../utils/customHooks/useColumns";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import { errorToast, successToast } from "../../../../utils/toastHelper";
import FileUploadField from "../../../Components/Inputs/FileUploadField";
import NameCell from "../../../Components/ProGrid/components/NameCell";
import { EmployeeDocumentsFormConfig } from "../EmployeeData";
import { createODataQuery } from "../../../../utils/services/oData";
import { useSelector } from "react-redux";
import { subModPermissionSelector } from "../../../store/features/permissions/permissionsSlice";
import { R_ALL_DOCUMENTS, R_DOCUMENTS_671_FORM } from "../../../../utils/permissionReferenceConfig";

export default function Documents() {
  let { id } = useParams();
  const [showGrid, setShowGrid] = useState(true);
  const [formConfig, setFormConfig] = useState(prepareInitialConfig(EmployeeDocumentsFormConfig));
  const [employeeDocumentData, setEmployeeDocumentData] = useState({});
  const [actionData, setActionData] = useState({});
  const [documentTypes, setDocumentTypes] = useState([]);
  const [isAddMode, setIsAddMode] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const {
    currentPage,
    pageSize,
    data: documentData,
    rowCount,
    status,
    sortModel,
    searchString,
    setPageState,
    setPagination,
    setStatus,
    setSortModel,
    setSearchString
  } = usePageState();
  const { [employeeDocumentsFormName]: employees } = usePermission(employeesDocumentsPermissionSelector, employeeDocumentsFormName);

  const { canExport: canExport671 } = useSelector(subModPermissionSelector(R_ALL_DOCUMENTS, R_DOCUMENTS_671_FORM));
  
  const columns = useColumn([
    {
      field: "documentName",
      headerName: "Document Name",
      renderCell: params => <NameCell params={params} onClick={onDocumentClick} />
    },
    {
      field: "employeeDocumentType",
      headerName: "Document Type"
    },
    {
      field: "employeeDocumentId",
      headerName: "Actions",
      valueGetter: params => {
        const is671Form = params.row.documentName?.toLowerCase().includes("671");
        if (is671Form && !canExport671) return ""; 
        return params.row.employeeDocumentId;
      },
      width: 160,
      renderCell: params => {
      const is671Form = params.row.documentName?.toLowerCase().includes("671");
      const isExportDisabled = is671Form && !canExport671;

    return (
      <>
        <Tooltip title="Download" arrow>
          <IconButton onClick={() => handleDownload(params)} color="primary" disabled={isExportDisabled} sx={{opacity: isExportDisabled ? 0 : 1, cursor: isExportDisabled ? "not-allowed" : "pointer"}}>
            <FileDownloadIcon fontSize="small" />
          </IconButton>
        </Tooltip>

        <DeleteCell params={params} onDelete={() => onDelete(params.row.employeeDocumentId)}/>
      </>
    );
  }
    }
  ], true);

  const { handleSubmit, control, setValue } = useForm({ defaultValues: prepareDefaultValues(EmployeeDocumentsFormConfig) });
  const isEditModeOn = useMemo(() => Object.values(formConfig).some(({ mode }) => mode === "edit"), [formConfig]);

  const onEdit = () => {
    setFormConfig(changeMode("edit"));
  };

  const handleReadValueClick = name => setFormConfig(changeModeForField(name, "edit"));

  const resetForm = () => {
    setActionData({});
    setShowGrid(true);
    setFormConfig(changeMode());
  };

  const onCancel = () => {
    if (actionData?.employeeDocumentId) {
      setFormConfig(changeMode("read"));
    } else {
      resetForm();
    }
  };

  const addButtonAction = () => {
    Object.keys(formConfig).forEach(field => setValue(field, null));
    onEdit();
    setShowGrid(false);
    setIsAddMode(true);
  };

  const onDocumentClick = params => {
    setShowGrid(false);
    setActionData({ ...params.row, documentMediaModel: params.row.documentMediaModel.path });
    getDocumentData(params.row);
  };

  const getAllDocuments = useCallback(async () => {
    setStatus(STAUTES.LOADING);
    const searchableFields = ['employeeDocumentType', "documentName"]
    const { data, totalRecords, error } = await getApi(EMPLOYEE_DOCUMENTS.replace("{employeeId}", id) + createODataQuery(searchableFields, [], [], searchString, sortModel, pageSize, currentPage, true));
    setStatus(STAUTES.IDLE);
    if (error) return errorToast(error);
    setPageState(prevPageInfo => ({ ...prevPageInfo, data: data || [], rowCount: totalRecords }));
  }, [currentPage, pageSize, sortModel, searchString, setPageState, setStatus]);

  const getAllDocumentTypes = async () => {
    const { data } = await getApi(EMPLOYEE_DOCUMENT_TYPES);
    setDocumentTypes(data.map(item => ({ label: item.name, value: item.id })));
  };

  const getDocumentData = async updateData => {
    const data = {
      documentName: updateData?.documentName,
      employeeDocumentTypeId: updateData?.employeeDocumentTypeId,
      documentMediaModel: updateData?.documentMediaModel?.path
    };
    setEmployeeDocumentData(data);
    Object.keys(formConfig).forEach(field => setValue(field, field === "documentMediaModel" ? null : data[field]));
  };

  const handleDownload = params => {
    const file = params.row.documentMediaModel?.path;
    const link = document.createElement("a");
    link.href = file;
    link.target = "_blank";
    link.rel = "noopener noreferrer";
    link.click();
  };

  const onDelete = async documentId => {
    const { error } = await deleteApi(`${EMPLOYEE_DOCUMENTS.replace("{employeeId}", id)}/${documentId}`);
    if (error) return errorToast(error);
    getAllDocuments();
    successToast("Employee document deleted successfully.");
  };

  const onSave = async data => {
    if (isSaving) return;
    setIsSaving(true);
    const base64ProfilePicture = await convertFileToBase64Model(data.documentMediaModel);
    const payload = {
      employeeDocumentTypeId: data?.employeeDocumentTypeId,
      employeeId: id,
      documentName: data?.documentName,
      documentMediaModel: base64ProfilePicture || null,
      employeeDocumentId: actionData?.employeeDocumentId
    };
    const { data: employeeDocument, error } = await postApi(actionData?.employeeDocumentId ? UPDATE_EMPLOYEE_DOCUMENT : ADD_EMPLOYEE_DOCUMENT, payload);
    setIsSaving(false);
    if (error) return errorToast(error);
    getDocumentById(employeeDocument?.guidId || actionData?.employeeDocumentId);
    getAllDocuments();
    successToast(`Employee Document ${actionData?.employeeDocumentId ? "Updated" : "Added"} Successfully`);
    setFormConfig(changeMode("read"));
  };

  const getDocumentById = async id => {
    const payload = { employeeDocumentId: id };
    const { error, data: employeeDocumentById } = await postApi(GET_EMPLOYEE_DOCUMENTS_BY_ID, payload);
    if (error) return errorToast(error);
    const data = {
      documentName: employeeDocumentById?.documentName,
      employeeDocumentTypeId: employeeDocumentById?.employeeDocumentTypeId,
      documentMediaModel: employeeDocumentById?.documentMediaModel?.path
    };
    setActionData(employeeDocumentById);
    setIsAddMode(false);
    setEmployeeDocumentData(data);
    Object.keys(formConfig).forEach(field => setValue(field, field === "documentMediaModel" ? null : data[field]));
  };

  useEffect(() => {
    getAllDocuments();
    getAllDocumentTypes();
  }, [getAllDocuments]);

  const defaultFormProps = { control, formValues: employeeDocumentData, isAddMode, handleReadValueClick };

  return (
    <TabGridAndFormLayout
      showGrid={showGrid}
      title={"Document"}
      backAction={() => resetForm()}
      backLabel="Go back to Documents List"
      beingEdited={isEditModeOn}
      onEdit={onEdit}
      canView={true}
      hideEdit={true}
      onSave={handleSubmit(onSave)}
      onCancel={onCancel}
      isSaving={isSaving}
    >
      {showGrid && (
        <ProGrid
          title="Documents"
          loading={status === STAUTES.LOADING}
          options={{
            getRowId: row => row.employeeDocumentId,
            rowCount: rowCount,
            paginationMode: "server",
            paginationModel: { pageSize: pageSize, page: currentPage },
            onPaginationModelChange: setPagination,
            sortingMode: "server",
            onSortModelChange: setSortModel
          }}
          columns={columns}
          hideExport={!employees.canExport}
          addButtonLabel="+ Add Document"
          rows={documentData}
          searchMode={!!searchString}
          searchModel={{ defaultValue: searchString, handleDebounce: setSearchString }}
          addButtonAction={addButtonAction}
        />
      )}
      {!showGrid && (
        <>
          <ProTextInput
            size={4}
            {...defaultFormProps}
            {...formConfig.documentName}
            permission={fieldTruePermission}

          />
          <ProSelectField
            {...defaultFormProps}
            {...formConfig.employeeDocumentTypeId}
            options={documentTypes}
            permission={fieldTruePermission}
          />
          <FileUploadField
            {...defaultFormProps}
            {...formConfig.documentMediaModel}
            inputProps={{ accept: "file/*" }}
            permission={fieldTruePermission}
          />
        </>
      )}
    </TabGridAndFormLayout>
  );
}
